import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Typeahead} from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router';
import {BaseComponent} from 'web/components/BaseComponent';
import {
  CASE_PRIORITY_HIGH,
  CASE_PRIORITY_NORMAL,
  CASE_STATUS_CLOSE,
  CASE_STATUS_OPEN,
} from 'web/constant';
import enumList from 'web/utils/enumList';
import {showMessage} from 'web/utils/modalHelper';
import {useCaseDetail} from './hooks/useCaseDetail';

const CaseDetailPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [uploadFiles, setUploadFiles] = useState([]);
  const [deleteFiles, setDeleteFiles] = useState([]);
  const [invalidShow, setInvalidShow] = useState('none');

  const {setSelectedCustomerData = () => {}, onOpenChange, caseNumber} = props;

  const {
    appearances,
    appearanceSelection,
    customerData,
    formValues,
    caseDetailFiles,
    customerSelection,
    conditions,
    setCaseDetailFiles,
    setAppearances,
    selectedMachines,
    setSelectedMachines,
    relCustomerMachineSelection,
    assignments,
    setCustomerData,
    setFormValues,
    setConditions,
    getCaseByCaseNumber,
    getCustomerSelection,
    getRelCustomerMachineSelection,
    handleEditCase,
    goBack,
    checkCoordinatesInfo,
    getAppearanceList,
    reportAppearances,
    handleDownloadFile,
    isRequiredAppearances,
  } = useCaseDetail(dispatch, showMessage, setSelectedCustomerData, caseNumber);

  const updateFormValues = (updatedValues) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      ...updatedValues,
    }));
  };

  const handleChange = (event) => {
    const {name, value} = event.target;

    updateFormValues({
      [name]: value,
    });
  };

  const handleUploadChange = (event) => {
    const {files} = event.target;
    const file = files[0];
    if (file) {
      setUploadFiles([...uploadFiles, file]);
    }
  };

  const handleConditionChange = (event) => {
    const {value} = event.target;

    if (conditions.includes(value)) {
      const filteredCoditions = conditions.filter((item) => item !== value);
      setConditions(filteredCoditions);
      // appearance 反向綁定
      const nextAppearanceState = appearances.filter((appearance) => {
        const skills = appearanceSelection.find(
          (item) => item.code === appearance.code,
        ).skills;
        return skills.some((skill) =>
          filteredCoditions.includes(skill.condition_code),
        );
      });

      setAppearances(nextAppearanceState);
    } else {
      setConditions([...conditions, value]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;

    const missingAppearanceData =
      isRequiredAppearances &&
      appearances.length === 0 &&
      conditions.length === 0;

    if (form.checkValidity() === false || missingAppearanceData) {
      setInvalidShow('block');
      return;
    }

    form.classList.add('was-validated');

    const formData = new FormData(form);

    const req = {
      casePriority: formValues.casePriority,
      caseNumber: formValues.caseNumber,
      customerCode: customerData[0].customerCode,
      description: formData.get('description'),
      purpose: formData.get('purpose'),
      deleteFiles,
      machineIds: selectedMachines,
      conditions,
      appearances: appearances.map(({id}) => id),
    };

    if (!(await checkCoordinatesInfo(req.customerCode))) {
      return onOpenChange(true);
    }

    await handleEditCase(req, uploadFiles);

    resetForm(form);
    navigate('/module/cases');
  };

  const resetForm = (form) => {
    form.classList.remove('was-validated');
    setUploadFiles([]);
    setDeleteFiles([]);
  };

  // 刪除新上傳的檔案
  const handleDeleteFile = (fileName) => {
    const files = uploadFiles.filter((file) => file.name !== fileName);
    setUploadFiles(files);
  };

  // 刪除已上傳的檔案
  const handleDeleteDetailFile = (fileToDelete) => {
    setCaseDetailFiles((prevFiles) =>
      prevFiles.filter((file) => file.filePath !== fileToDelete.filePath),
    );
    setDeleteFiles((prevFiles) => [...prevFiles, fileToDelete.fileName]);
  };

  const checkIsOpen = () => {
    return formValues.flgStatus === enumList.caseFlgStatus.close;
  };

  // 過濾掉已選取的選項
  const availableOptions = appearanceSelection.filter(
    (option) => !appearances.some((selected) => selected.code === option.code),
  );

  const handleAppearanceChange = (selectes) => {
    // 判斷該操作新增還是移除

    if (selectes.length > appearances.length) {
      const currentCode = selectes.find(
        (select) => !appearances.includes(select),
      ).code;

      const currentSelected = appearanceSelection.find(
        (select) => select.code === currentCode,
      );

      loadConditionsByAppearanceType(currentSelected);
    } else {
      const currentCode = appearances.find(
        (select) => !selectes.includes(select),
      ).code;

      const currentSelected = appearanceSelection.find(
        (select) => select.code === currentCode,
      );
      unloadConditionsByAppearance(currentSelected);
    }
    setAppearances(selectes);
  };

  const unloadConditionsByAppearance = (currentSelected) => {
    const clearSelection = currentSelected.skills
      .map((item) => item.condition_code)
      .flat();
    setConditions((prev) =>
      prev.filter((condition) => !clearSelection.includes(condition)),
    );
  };

  const loadConditionsByAppearanceType = (currentSelected) => {
    const appearanceSelection = currentSelected.skills
      .map((item) => item.condition_code)
      .flat();

    const nextState = [...conditions, ...appearanceSelection];
    setConditions([...new Set(nextState)]);
  };

  const availableSkills = useMemo(() => {
    // 渲染指派條件用，讓UI不那麼亂
    if (!appearances.length) return [];

    return appearances
      .map((condition) => {
        const renderItem = appearanceSelection.find(
          (appearance) => condition.code === appearance.code,
        );
        return renderItem?.skills || [];
      })
      .flat();
  }, [appearances, appearanceSelection]);

  const handleMachineCheckboxChange = useCallback(
    (event) => {
      /** @type {HTMLInputElement} */
      const currentTarget = event.target;
      const currentValue = parseInt(currentTarget.value);
      if (currentTarget.checked) {
        setSelectedMachines([...selectedMachines, currentValue].sort());
      } else {
        setSelectedMachines(
          selectedMachines.filter((machineID) => machineID !== currentValue),
        );
      }
    },
    [selectedMachines],
  );
  useEffect(() => {
    const baseComponent = new BaseComponent(props);
    baseComponent.checkPermission(
      enumList.moduleEnums.case,
      enumList.accessModeEnums.read,
    );
    getCustomerSelection();
    getAppearanceList();
    getCaseByCaseNumber();
  }, []);

  useEffect(() => {
    if (customerData?.[0]?.customerCode) {
      getRelCustomerMachineSelection();
    }
  }, [customerData]);

  useEffect(() => {
    if (formValues.appearanceType) {
      loadConditionsByAppearanceType();
    }
  }, [formValues.appearanceType]);

  return (
    <section id="datatable-container">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="f-fs-sm btn" onClick={goBack}>
            <i className="bi bi-arrow-left me-1"></i>
            回上頁
          </li>
        </ol>
      </nav>
      <form
        className=" needs-validation"
        id="modalResidentForm"
        noValidate
        onSubmit={handleSubmit}
      >
        <div className="modal-body">
          <div className="d-flex flex-column gap-3 px-2">
            <div className="modal__form__catagory row">
              <div className="col-12">
                <div className="d-flex gap-3">
                  <div
                    className={`status status-${
                      checkIsOpen() ? 'normal' : 'danger'
                    } mb-2`}
                  >
                    <span>
                      {checkIsOpen() ? CASE_STATUS_CLOSE : CASE_STATUS_OPEN}
                    </span>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <label
                      htmlFor="casePriority"
                      className="form-label me-auto"
                    >
                      案件等級
                    </label>
                    <div
                      className="btn-group btn-group-sm w-100"
                      role="group"
                      aria-label="radio toggle button group"
                    >
                      <input
                        type="radio"
                        className="btn-check"
                        name="casePriority"
                        id="priorityNormal"
                        autoComplete="off"
                        disabled={
                          formValues.flgStatus === enumList.caseFlgStatus.close
                        }
                        value={enumList.casePriority.normal}
                        checked={
                          Number(formValues.casePriority) ===
                          enumList.casePriority.normal
                        }
                        onChange={handleChange}
                        data-checkbox-group="required"
                      />
                      <label
                        className="btn btn-outline-secondary"
                        htmlFor="priorityNormal"
                      >
                        一般
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="casePriority"
                        id="priorityHigh"
                        autoComplete="off"
                        value={enumList.casePriority.high}
                        checked={
                          Number(formValues.casePriority) ===
                          enumList.casePriority.high
                        }
                        disabled={
                          formValues.flgStatus === enumList.caseFlgStatus.close
                        }
                        onChange={handleChange}
                        data-checkbox-group="required"
                      />
                      <label
                        className="btn btn-outline-secondary"
                        htmlFor="priorityHigh"
                      >
                        <svg className="btn__icon" aria-hidden="true">
                          <use href="#ico-zap"></use>
                        </svg>
                        緊急
                      </label>
                    </div>
                  </div>
                  <div className="col">
                    <label htmlFor="caseType" className="form-label me-auto">
                      案件服務區分
                    </label>
                    <select
                      id="caseType"
                      name="caseType"
                      className="form-select form-select-sm"
                      value={formValues.caseType}
                      disabled
                    >
                      <option value="" disabled>
                        請選擇
                      </option>
                      {Object.values(enumList.CaseTypeEnums).map(
                        ({value, stringValue}) => (
                          <option key={value} value={value}>
                            {stringValue}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div className="col">
                    <label htmlFor="caseNumber" className="form-label">
                      立案編號
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      id="caseNumber"
                      name="caseNumber"
                      value={formValues.caseNumber}
                      required
                      disabled
                    />
                  </div>
                  <div className="col">
                    <label htmlFor="createTime" className="form-label">
                      立案時間
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      id="createTime"
                      name="createTime"
                      value={formValues.createTime}
                      required
                      disabled
                    />
                  </div>
                  <div className="col">
                    <label htmlFor="customerName" className="form-label">
                      客戶名稱
                    </label>
                    <Typeahead
                      id="customerName"
                      size="sm"
                      onChange={setCustomerData}
                      selected={customerData}
                      value={customerData}
                      options={customerSelection}
                      labelKey="customerName"
                      placeholder="請輸入客戶名稱"
                      disabled
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label htmlFor="uploadFile" className="form-label">
                      上傳附件
                    </label>
                    <input
                      type="file"
                      className="form-control form-control-sm"
                      disabled={
                        formValues.flgStatus === enumList.caseFlgStatus.close
                      }
                      id="uploadFile"
                      name="uploadFile"
                      onChange={handleUploadChange}
                      accept=".pdf"
                    ></input>
                    <div className="d-flex gap-2 flex-wrap my-2">
                      {caseDetailFiles.length > 0 &&
                        caseDetailFiles.map((file, index) => (
                          <button
                            type="button"
                            key={index}
                            className="btn btn-outline-secondary rounded-pill"
                            onClick={() => {
                              console.log('click');
                            }}
                          >
                            <svg className="btn__icon me-1" aria-hidden="true">
                              <use href="#ico-file-04"></use>
                            </svg>
                            {file.fileName}
                            <i
                              class="bi bi-file-earmark-arrow-down ms-3"
                              style={{
                                color: 'var(--ce-colors-gray-light-mode--700)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadFile(file.fileName);
                              }}
                            />
                            <i
                              className="bi bi-x-lg ms-2"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteDetailFile(file);
                              }}
                              style={{
                                color: 'var(--ce-colors-gray-light-mode--700)',
                              }}
                            ></i>
                          </button>
                        ))}
                      {uploadFiles.length > 0 &&
                        uploadFiles.map((file, index) => (
                          <button
                            type="button"
                            key={index}
                            className="btn btn-outline-secondary rounded-pill"
                          >
                            <svg className="btn__icon me-1" aria-hidden="true">
                              <use href="#ico-file-04"></use>
                            </svg>
                            {file.name}
                            <i
                              className="bi bi-x-lg ms-3"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteFile(file.name);
                              }}
                              style={{
                                color: 'var(--ce-colors-gray-light-mode--700)',
                              }}
                            ></i>
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label className="form-label mt-2">客戶機號</label>
                    <div className="row g-2">
                      {relCustomerMachineSelection.map((machine, index) => (
                        <div className="col-4" key={index}>
                          <input
                            type="checkbox"
                            className="btn-check"
                            id={`machine${machine.machineSerialNumber}`}
                            value={machine.machineId}
                            onChange={handleMachineCheckboxChange}
                            checked={selectedMachines.includes(
                              machine.machineId,
                            )}
                            disabled={
                              ![undefined, 'A'].includes(
                                assignments.get(machine.machineId)?.flgStatus,
                              ) ||
                              formValues.flgStatus ===
                                enumList.caseFlgStatus.close
                            }
                          />
                          <label
                            className="btn btn-sm btn-outline-secondary"
                            htmlFor={`machine${machine.machineSerialNumber}`}
                          >
                            {`${machine.machineTypeName} - ${machine.machineSerialNumber}`}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label htmlFor="appearanceType" className="form-label mt-2">
                      現象選擇
                    </label>
                    <div className="row g-2">
                      <Typeahead
                        id="appearance-name"
                        size="sm"
                        onChange={handleAppearanceChange}
                        multiple
                        selected={appearances}
                        options={availableOptions}
                        labelKey="name"
                        placeholder="請輸入現象名稱"
                        disabled={
                          formValues.flgStatus === enumList.caseFlgStatus.close
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <label htmlFor="caseDetailCondition" className="form-label">
                      指派條件
                    </label>
                    <div className="row g-2">
                      {appearances.length > 0 &&
                        availableSkills.map((skill, index) => (
                          <div className="col-4" key={index}>
                            <input
                              type="checkbox"
                              className="btn-check"
                              name="modalApplyCondition"
                              id={`modalApplyCondition${skill.condition_code}`}
                              autoComplete="off"
                              value={skill.condition_code}
                              onChange={handleConditionChange}
                              checked={conditions.includes(
                                skill.condition_code,
                              )}
                              disabled={
                                formValues.flgStatus ===
                                enumList.caseFlgStatus.close
                              }
                            />
                            <label
                              className="btn btn-sm btn-outline-secondary"
                              htmlFor={`modalApplyCondition${skill.condition_code}`}
                            >
                              {skill.condition_name}
                            </label>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div className="col-12">
                    <label htmlFor="description" className="form-label">
                      案件目的
                    </label>
                    <textarea
                      className="form-control"
                      id="purpose"
                      name="purpose"
                      value={formValues.purpose}
                      onChange={handleChange}
                      rows="2"
                      disabled={
                        formValues.flgStatus === enumList.caseFlgStatus.close
                      }
                    ></textarea>
                  </div>
                  <div className="col-12">
                    <label htmlFor="description" className="form-label">
                      案件內容
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      value={formValues.description}
                      onChange={handleChange}
                      rows="2"
                    ></textarea>
                  </div>
                  {reportAppearances.size > 0 && (
                    <div className="col-12">
                      <label htmlFor="reportAppearance" className="form-label">
                        案件現象
                      </label>
                      <textarea
                        className="form-control"
                        id="reportAppearance"
                        name="reportAppearance"
                        value={[...reportAppearances]
                          .map(([key, value]) => `${key}: ${value}`)
                          .join('\n')}
                        rows="2"
                        readOnly
                      ></textarea>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <div
                  className="alert alert-warning global-invalid-feedback"
                  style={{display: invalidShow}}
                ></div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end w-100">
          <button type="button" className="btn btn-cancel" onClick={goBack}>
            取消
          </button>
          <button type="submit" className="btn btn-primary">
            儲存
          </button>
        </div>
      </form>
    </section>
  );
};

export default CaseDetailPage;
